'use client';
import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useRouter } from 'next/navigation';
import { Eye, EyeOff, Loader2, LogIn } from 'lucide-react';
import { toast } from 'sonner';
import AppLogo from '@/components/ui/AppLogo';
import { createClient } from '@/lib/supabase/client';

interface LoginFormValues {
  email: string;
  password: string;
  remember: boolean;
}

export default function LoginForm() {
  const router = useRouter();
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const {
    register,
    handleSubmit,
    setError,
    formState: { errors },
  } = useForm<LoginFormValues>({
    defaultValues: { email: '', password: '', remember: false },
  });

  const onSubmit = async (data: LoginFormValues) => {
    setIsLoading(true);
    try {
      const supabase = createClient();
      const { data: authData, error } = await supabase.auth.signInWithPassword({
        email: data.email,
        password: data.password,
      });

      if (error) {
        setIsLoading(false);
        setError('email', {
          type: 'manual',
          message: 'Ongeldige inloggegevens. Controleer uw e-mailadres en wachtwoord.',
        });
        return;
      }

      // Determine role from user metadata
      const role = authData.user?.user_metadata?.role || authData.user?.app_metadata?.role || 'medewerker';
      const mustChange = authData.user?.user_metadata?.must_change_password === true;

      if (role === 'admin') {
        toast.success('Welkom terug! Ingelogd als beheerder.');
        router.push('/admin-dashboard');
      } else if (mustChange) {
        toast.info('Wijzig uw wachtwoord om door te gaan.');
        router.push('/change-password');
      } else {
        const naam = authData.user?.user_metadata?.naam || data.email.split('@')[0];
        toast.success(`Welkom terug, ${naam}!`);
        router.push('/employee-dashboard');
      }
    } catch {
      setIsLoading(false);
      setError('email', {
        type: 'manual',
        message: 'Er is een fout opgetreden. Probeer opnieuw.',
      });
    }
  };

  return (
    <div className="w-full max-w-md animate-fade-in">
      {/* Mobile logo */}
      <div className="flex items-center gap-2 mb-8 lg:hidden">
        <AppLogo size={36} />
        <span className="font-semibold text-slate-800 text-lg">Rho Bouwtechniek</span>
      </div>

      <div className="mb-8">
        <h2 className="text-2xl font-700 text-slate-800 mb-1.5">Inloggen</h2>
        <p className="text-[14px] text-slate-500">Toegang tot uw medewerkers- of beheerdersportaal</p>
      </div>

      <form onSubmit={handleSubmit(onSubmit)} noValidate className="space-y-5">
        {/* Email */}
        <div>
          <label htmlFor="email" className="block text-[13px] font-500 text-slate-700 mb-1.5">
            E-mailadres
          </label>
          <input
            id="email"
            type="email"
            autoComplete="email"
            placeholder="naam@bedrijf.nl"
            {...register('email', {
              required: 'E-mailadres is verplicht',
              pattern: { value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/, message: 'Ongeldig e-mailadres' },
            })}
            className={`w-full px-3.5 py-2.5 rounded-lg border text-[14px] text-slate-800 placeholder-slate-400 bg-white outline-none transition-all
              ${errors.email
                ? 'border-red-400 ring-2 ring-red-100 focus:border-red-400' : 'border-slate-300 focus:border-blue-500 focus:ring-2 focus:ring-blue-100'
              }`}
          />
          {errors.email && (
            <p className="mt-1.5 text-[12px] text-red-600 flex items-center gap-1">
              <span>⚠</span> {errors.email.message}
            </p>
          )}
        </div>

        {/* Password */}
        <div>
          <div className="flex items-center justify-between mb-1.5">
            <label htmlFor="password" className="block text-[13px] font-500 text-slate-700">
              Wachtwoord
            </label>
          </div>
          <div className="relative">
            <input
              id="password"
              type={showPassword ? 'text' : 'password'}
              autoComplete="current-password"
              placeholder="••••••••••"
              {...register('password', { required: 'Wachtwoord is verplicht' })}
              className={`w-full px-3.5 py-2.5 pr-10 rounded-lg border text-[14px] text-slate-800 placeholder-slate-400 bg-white outline-none transition-all
                ${errors.password
                  ? 'border-red-400 ring-2 ring-red-100' : 'border-slate-300 focus:border-blue-500 focus:ring-2 focus:ring-blue-100'
                }`}
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 transition-colors"
              aria-label={showPassword ? 'Verberg wachtwoord' : 'Toon wachtwoord'}
            >
              {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
            </button>
          </div>
          {errors.password && (
            <p className="mt-1.5 text-[12px] text-red-600">⚠ {errors.password.message}</p>
          )}
        </div>

        {/* Remember me */}
        <div className="flex items-center gap-2.5">
          <input
            id="remember"
            type="checkbox"
            {...register('remember')}
            className="w-4 h-4 rounded border-slate-300 text-blue-600 accent-blue-600 cursor-pointer"
          />
          <label htmlFor="remember" className="text-[13px] text-slate-600 cursor-pointer select-none">
            Onthoud mij op dit apparaat
          </label>
        </div>

        {/* Submit */}
        <button
          type="submit"
          disabled={isLoading}
          className="w-full flex items-center justify-center gap-2 bg-blue-600 hover:bg-blue-700 active:scale-[0.98] disabled:opacity-70 disabled:cursor-not-allowed text-white font-600 text-[14px] py-2.5 rounded-lg transition-all duration-150 shadow-sm"
          style={{ minHeight: '44px' }}
        >
          {isLoading ? (
            <>
              <Loader2 size={16} className="animate-spin" />
              <span>Inloggen...</span>
            </>
          ) : (
            <>
              <LogIn size={16} />
              <span>Inloggen</span>
            </>
          )}
        </button>
      </form>
    </div>
  );
}